import { ApolloClient, NormalizedCacheObject, useMutation, useQuery } from '@apollo/client';
import {
  UpdateUserProfileFieldsData,
  UpdateUserProfileFieldsVars,
  UserProfileFieldsData,
  UserProfileFieldsVars
} from '../../membership/gql-types';
import { cloneDeep, isFunction, isUndefined } from 'lodash';
import { useEffect, useState } from 'react';

import ApolloClientService from '../services/ApolloClientService';
import Config from '../config';
import GraphQL from '../../membership/gql';
import Profile from '../models/Profile';
import ProfileFieldInputVm from '../models/ProfileFieldInputVm';
import { ProfileFieldType } from '../../shared/models/ProfileField';
import ProfileService from '../services/ProfileService';
import useProfileData from './useProfileData';

let membershipApolloClient: ApolloClient<NormalizedCacheObject> = null;

/** Returns a value indicating whether the field is the Profile Image field. */
const isAvatarProfile = (p: Profile) =>
  p.title === 'Profile Image' &&
  p.fields.length === 1 &&
  p.fields[0]?.type === ProfileFieldType.IMAGE;

export interface UseUserProfileOptions {
  onSaveComplete?: () => void;
  /** Value indicating whether validation should be for registration page. */
  forRegistration?: boolean;
}

function useUserProfile(userkey?: number, options?: UseUserProfileOptions) {
  const [profileFields, setProfileFields] = useState<Profile[]>();
  const [initialProfileFieldInputs, setInitialProfileFieldInputs] =
    useState<ProfileFieldInputVm[]>();
  const [profileFieldInputs, setProfileFieldInputs] =
    useState<ProfileFieldInputVm[]>();
  const [allRequiredFilledIn, setAllRequiredFilledIn] = useState<boolean>();
  const [anyChanges, setAnyChanges] = useState(false);

  if (!membershipApolloClient) {
    membershipApolloClient = ApolloClientService.create(Config.MEMBERSHIP_GQL);
  }

  useEffect(() => {
    const ok = ProfileService.allRequiredProfileFieldsEntered(
      profileFields,
      profileFieldInputs
    );
    setAllRequiredFilledIn(ok);
  }, [profileFields, profileFieldInputs]);

  useEffect(() => {
    if (initialProfileFieldInputs && profileFieldInputs) {
      setAnyChanges(
        ProfileService.anyChanges(initialProfileFieldInputs, profileFieldInputs)
      );
    }
  }, [profileFieldInputs]);

  const { profileLoading, profileError, profileData } = useProfileData();

  const {
    called: profileValuesCalled,
    loading: profileValuesLoading,
    error: profileValuesError,
    data: profileValuesData
  } = useQuery<UserProfileFieldsData, UserProfileFieldsVars>(
    GraphQL.Query.USER_PROFILE_FIELDS,
    {
      client: membershipApolloClient,
      variables: {
        userkey
      },
      fetchPolicy: 'no-cache',
      skip: !membershipApolloClient || !userkey
    }
  );

  function handleProfileChange(input: ProfileFieldInputVm) {
    const previousInput = cloneDeep(profileFieldInputs);
    setInitialProfileFieldInputs(previousInput);
    setProfileFieldInputs((value) => {
      if (!value) {
        return [input];
      }
      const fieldIndex = value.findIndex(
        (field) =>
          field.profilekey === input.profilekey &&
          field.fieldposition === input.fieldposition
      );
      if (fieldIndex >= 0) {
        value.splice(fieldIndex, 1, input);
      } else {
        value.push(input);
      }
      return cloneDeep(value);
    });
  }

  const [
    updateUserProfileFields,
    {
      called: updateCalled,
      loading: updating,
      error: updateError,
      data: updateData
    }
  ] = useMutation<UpdateUserProfileFieldsData, UpdateUserProfileFieldsVars>(
    GraphQL.Mutation.UPDATE_USER_PROFILE_FIELDS,
    {
      client: membershipApolloClient,
      onCompleted(data) {
        setAnyChanges(false);
        if (isFunction(options?.onSaveComplete)) {
          options.onSaveComplete();
        }
      },
      refetchQueries: [{
        query: GraphQL.Query.USER_PROFILE_FIELDS,
        variables:{userkey}
      }],
      awaitRefetchQueries: true 
    }
  );

  async function saveFieldValuesAsync(
    userkey: number,
    profileFieldInputs: ProfileFieldInputVm[]
  ) {
    return await updateUserProfileFields({
      variables: {
        userkey,
        profileFieldInputs:
          ProfileService.convertToProfileFieldInputs(profileFieldInputs)
      }
    });
  }

  if (profileData && !profileFields) {
    setProfileFields(
      options?.forRegistration
        ? profileData.profile.filter((p) => !isAvatarProfile(p))
        : profileData?.profile
    );
  }

  if (profileFields && profileValuesData && !profileFieldInputs) {
    const inputs:ProfileFieldInputVm[] = [];
    for (let profile of profileFields) {
      inputs.push(
        ...ProfileService.convertValuesToInputs(
          profile,
          profileValuesData.userProfileFields
        )
      );
    }
    if (isUndefined(initialProfileFieldInputs)) {
      setInitialProfileFieldInputs(cloneDeep(inputs));
    }
    setProfileFieldInputs(inputs);
  }

  const avatarProfile = profileFields?.find(isAvatarProfile);
  const avatarProfileField = avatarProfile && {
    ...avatarProfile.fields[0],
    required: avatarProfile.required
  };

  return {
    handleProfileChange,
    saveFieldValuesAsync,
    loading: profileLoading || (!!userkey && profileValuesLoading),
    loadError: profileError || (!!userkey && profileValuesError),
    saving: updating,
    saveError: updateError,
    profileFields,
    avatarProfileField,
    userProfileFields: profileValuesData?.userProfileFields,
    profileFieldInputs,
    allRequiredFilledIn,
    anyChanges
  };
}

export default useUserProfile;
